import { useState, useEffect } from "react";
import { useOutletContext, Link } from "react-router-dom";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Users,
  BookOpen,
  Activity,
  ChevronRight,
  GraduationCap
} from "lucide-react";

export default function TeacherClasses() {
  const { teacher, school } = useOutletContext();
  const [classes, setClasses] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchClasses();
  }, []);

  const fetchClasses = async () => {
    try {
      const response = await fetch(`${API}/teacher/dashboard/classes`, {
        credentials: "include"
      });
      
      if (response.ok) {
        const data = await response.json();
        setClasses(data.classes_by_grade || {});
      }
    } catch (error) {
      console.error("Failed to fetch classes:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-48 bg-gray-200 rounded-lg"></div>
          ))}
        </div>
      </div>
    );
  }

  const gradeKeys = Object.keys(classes).sort((a, b) => {
    const gradeA = parseInt(a.replace("Grade ", ""));
    const gradeB = parseInt(b.replace("Grade ", ""));
    return gradeA - gradeB;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">My Classes</h1>
        <p className="text-gray-500 mt-1">
          View your assigned grades and subjects
        </p>
      </div>

      {/* Classes by Grade */}
      {gradeKeys.length > 0 ? (
        <div className="space-y-6">
          {gradeKeys.map((gradeKey) => {
            const gradeData = classes[gradeKey];
            
            return (
              <Card key={gradeKey} className="border-0 shadow-md overflow-hidden">
                {/* Grade Header */}
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4 text-white">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                        <span className="text-xl font-bold">{gradeData.grade}</span>
                      </div>
                      <div>
                        <h2 className="text-xl font-bold">{gradeKey}</h2>
                        <p className="text-blue-100">
                          {gradeData.classes?.length || 0} subjects assigned
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold">{gradeData.total_students}</p>
                      <p className="text-blue-100 text-sm">students</p>
                    </div>
                  </div>
                </div>

                {/* Subjects */}
                <CardContent className="p-0">
                  <div className="divide-y divide-gray-100">
                    {gradeData.classes?.map((cls, index) => (
                      <Link
                        key={index}
                        to={`/teacher/students?grade=${cls.grade}&subject=${encodeURIComponent(cls.subject)}`}
                        className="block"
                      >
                        <div className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors">
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                              <BookOpen className="w-5 h-5 text-emerald-600" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{cls.subject}</p>
                              <div className="flex items-center gap-4 text-sm text-gray-500">
                                <span className="flex items-center gap-1">
                                  <Users className="w-4 h-4" />
                                  {cls.student_count} students
                                </span>
                                <span className="flex items-center gap-1">
                                  <Activity className="w-4 h-4 text-emerald-500" />
                                  {cls.active_today} active
                                </span>
                              </div>
                            </div>
                          </div>
                          <ChevronRight className="w-5 h-5 text-gray-400" />
                        </div>
                      </Link>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="border-0 shadow-md">
          <CardContent className="py-16 text-center">
            <GraduationCap className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Classes Assigned</h3>
            <p className="text-gray-500 max-w-md mx-auto">
              You don't have any classes assigned yet. Please contact your school administrator
              to get your grade and subject assignments.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Summary Stats */}
      {gradeKeys.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border-0 shadow-md">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <GraduationCap className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{gradeKeys.length}</p>
                <p className="text-sm text-gray-500">Grades Assigned</p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {Object.values(classes).reduce((sum, g) => sum + (g.classes?.length || 0), 0)}
                </p>
                <p className="text-sm text-gray-500">Total Classes</p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {Object.values(classes).reduce((sum, g) => sum + (g.total_students || 0), 0)}
                </p>
                <p className="text-sm text-gray-500">Total Students</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
